import {
  DialogContent,
  Dialog,
  DialogProps,
  DialogActions,
  useMediaQuery,
  useTheme,
  Grid,
} from '@mui/material';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { PmiButton } from 'shell/components/shared/button';
import { styled } from '@mui/styles';
import { memo, useState } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { useFormik } from 'formik';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { genericTransformer } from 'shell/utils/generic-transformer';
import { HttpResponse } from 'shell/models/http-response';
import SearchMessages, { Message, MessageType } from 'shell/components/shared/search-messages';
import { noop } from 'shell/utils/noop';
import ErrorModal from 'shell/components/shared/modal-error';

export const initialValuesNota = 'ANNULLAMENTO PRATICA PER RIPRISTINO RESIDENZA';

export const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '300px',
      },
}));

interface ModalN010ComponentProps {
  open: boolean;
  onSave: (nota: string) => void;
  onClose: () => void;
  isIrreper?: boolean;
}

const ModalN010Component = ({
  open,
  onSave,
  onClose,
  isIrreper = false,
}: ModalN010ComponentProps): React.ReactElement => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  const params = useParams();

  const [controlliIrreper] = useHttpFetch<HttpResponse<{ message: string }>, Message[] | undefined>(
    `${__PRACTICE_API_URL__}/controlli-pratiche/irreper-chiuse-collegate/${params.codiceEnte}-${params.tipoRegistro}-${params.idAnnoPratica}-${params.progressivoPratica}`,
    {
      onBootstrap: true,
      options: {
        method: 'GET',
        loading: true,
        errorCatching: true,
      },
      transformer: (res) => {
        const genericRes = genericTransformer(res);
        return genericRes.payload?.message
          ? [{ id: 1, value: genericRes.payload?.message || '' }]
          : undefined;
      },
    }
  );

  const saveNote = useHttpClient(`${__PRACTICE_API_URL__}/chiusura-pratica/ripristino-pratica`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const [restorationModal, setRestorationModal] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  const formik = useFormik<{ nota: string }>({
    initialValues: {
      nota: initialValuesNota,
    },
    onSubmit: (values) => {
      (
        saveNote({
          data: {
            codiceEnte: params.codiceEnte,
            tipoRegistro: params.tipoRegistro,
            idAnnoPratica: params.idAnnoPratica,
            progressivoPratica: params.progressivoPratica,
            descrizioneNote: values.nota,
          },
        }) as ResponsePromise<HttpResponse<{ message: string }>>
      ).then((response) => {
        const message = response?.data?.payload?.message ?? 'Ripristino completato con successo';
        setRestorationModal({ open: true, message });
        onSave(values.nota);
        toast.success('Operazione eseguita con successo');
      });
    },
  });

  const warnings = controlliIrreper.data as Message[];

  return (
    <>
      <ErrorModal
        title="Attenzione"
        message={restorationModal.message}
        open={restorationModal.open}
        onClose={() => setRestorationModal({ open: false, message: '' })}
      />
      <PmiDialog
        fullScreen={fullScreen}
        open={open}
        onClose={onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
          Nota di ripristino residenza
        </PmiDialogTitle>
        <DialogContent style={{ padding: '30px 24px' }}>
          <Grid container spacing={1}>
            {isIrreper ? undefined : (
              <Grid item xs={12}>
                <SearchMessages
                  type={MessageType.Warning}
                  title={`C'è ${warnings?.length || ''} warning`}
                  messages={warnings || []}
                  onChangeVisibility={noop}
                  visibility={!!warnings}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <PmiTextField
                value={formik.values.nota || ''}
                id="nota"
                name="nota"
                aria-label="nota"
                fullWidth
                minRows={5}
                multiline
                onChange={(event) => formik.setFieldValue('nota', event.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <PmiButton variant="contained" color="primary" onClick={() => formik.submitForm()}>
            CONFERMA
          </PmiButton>
          <PmiButton variant="outlined" color="cdmBrand" onClick={onClose}>
            ANNULLA
          </PmiButton>
        </DialogActions>
      </PmiDialog>
    </>
  );
};

ModalN010Component.displayName = 'Modal N010 component';

const ModalN010 = memo(ModalN010Component);

export default ModalN010;
